import { useState } from 'react';
import {
    MediaKitData,
    MediaKitHighlight,
    hasHighlightFields,
    updateMediaKit,
} from '../services/mediaKitService';
import {
    MEDIA_KIT_MAX_HIGHLIGHT_DESCRIPTION_CHARS,
    MEDIA_KIT_MAX_HIGHLIGHT_IMPACT_CHARS,
    MEDIA_KIT_MAX_HIGHLIGHT_TITLE_CHARS,
    truncateMediaKitField,
    validateMediaKitHighlightFields,
} from '../utils/mediaKitValidation';

const MEDIA_KIT_MAX_HIGHLIGHTS = 2;

export type HighlightDraft = {
    title: string;
    description: string;
    impact: string;
};

interface MediaKitHighlightsEditProps {
    highlights: MediaKitHighlight[];
    athleteUserId: number;
    onSaved: (data: MediaKitData) => void;
    onCancel: () => void;
}

const emptyHighlightDraft = (): HighlightDraft => ({
    title: '',
    description: '',
    impact: '',
});

function highlightsToDrafts(highlights: MediaKitHighlight[]): HighlightDraft[] {
    const sorted = [...highlights].sort((a, b) => a.displayOrder - b.displayOrder);
    const drafts = sorted.map((highlight) => ({
        title: highlight.title ?? '',
        description: highlight.description ?? '',
        impact: highlight.impact ?? '',
    }));

    while (drafts.length < MEDIA_KIT_MAX_HIGHLIGHTS) {
        drafts.push(emptyHighlightDraft());
    }

    return drafts.slice(0, MEDIA_KIT_MAX_HIGHLIGHTS);
}

export default function MediaKitHighlightsEdit({
    highlights,
    athleteUserId,
    onSaved,
    onCancel,
}: MediaKitHighlightsEditProps) {
    const [drafts, setDrafts] = useState<HighlightDraft[]>(() => highlightsToDrafts(highlights));
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    const updateDraft = (index: number, field: keyof HighlightDraft, value: string) => {
        setDrafts((prev) =>
            prev.map((draft, draftIndex) =>
                draftIndex === index ? { ...draft, [field]: value } : draft
            )
        );
        setSaveError(null);
    };

    const save = async () => {
        const filledDrafts = drafts.filter((draft) =>
            hasHighlightFields(draft.title, draft.description, draft.impact)
        );

        for (const draft of filledDrafts) {
            const validationError = validateMediaKitHighlightFields(
                draft.title,
                draft.description,
                draft.impact
            );
            if (validationError) {
                setSaveError(validationError);
                return;
            }
        }

        setSaving(true);
        setSaveError(null);
        try {
            const updated = await updateMediaKit(
                {
                    highlights: filledDrafts.map(({ title, description, impact }) => ({
                        title,
                        description,
                        impact,
                    })),
                },
                athleteUserId
            );
            onSaved(updated);
        } catch (e: unknown) {
            setSaveError(e instanceof Error ? e.message : 'Failed to save highlights');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="media-kit-highlights-edit" data-testid="media-kit-highlights-edit">
            <div className="media-kit-highlights-edit-grid">
                {drafts.map((draft, index) => (
                    <div key={index} className="media-kit-highlight-edit-card">
                        <h4 className="media-kit-highlight-edit-heading">
                            {index === 0 ? 'Highlight 1' : 'Highlight 2'}
                        </h4>
                        <div className="media-kit-field-group">
                            <label className="media-kit-field-label" htmlFor={`highlight-title-${index}`}>
                                Title of opportunity
                            </label>
                            <input
                                id={`highlight-title-${index}`}
                                className="media-kit-input"
                                value={draft.title}
                                onChange={(e) =>
                                    updateDraft(
                                        index,
                                        'title',
                                        truncateMediaKitField(e.target.value, MEDIA_KIT_MAX_HIGHLIGHT_TITLE_CHARS)
                                    )
                                }
                                maxLength={MEDIA_KIT_MAX_HIGHLIGHT_TITLE_CHARS}
                                data-testid={`media-kit-edit-highlight-title-${index}`}
                            />
                        </div>
                        <div className="media-kit-field-group">
                            <label className="media-kit-field-label" htmlFor={`highlight-description-${index}`}>
                                Describe what you did
                            </label>
                            <textarea
                                id={`highlight-description-${index}`}
                                className="media-kit-textarea"
                                value={draft.description}
                                onChange={(e) =>
                                    updateDraft(
                                        index,
                                        'description',
                                        truncateMediaKitField(
                                            e.target.value,
                                            MEDIA_KIT_MAX_HIGHLIGHT_DESCRIPTION_CHARS
                                        )
                                    )
                                }
                                maxLength={MEDIA_KIT_MAX_HIGHLIGHT_DESCRIPTION_CHARS}
                                data-testid={`media-kit-edit-highlight-description-${index}`}
                            />
                        </div>
                        <div className="media-kit-field-group">
                            <label className="media-kit-field-label" htmlFor={`highlight-impact-${index}`}>
                                Describe impact
                            </label>
                            <textarea
                                id={`highlight-impact-${index}`}
                                className="media-kit-textarea"
                                value={draft.impact}
                                onChange={(e) =>
                                    updateDraft(
                                        index,
                                        'impact',
                                        truncateMediaKitField(e.target.value, MEDIA_KIT_MAX_HIGHLIGHT_IMPACT_CHARS)
                                    )
                                }
                                maxLength={MEDIA_KIT_MAX_HIGHLIGHT_IMPACT_CHARS}
                                data-testid={`media-kit-edit-highlight-impact-${index}`}
                            />
                        </div>
                    </div>
                ))}
            </div>

            {saveError && <span className="media-kit-error">{saveError}</span>}

            <div className="media-kit-highlights-edit-actions">
                <button type="button" className="media-kit-btn-primary" onClick={save} disabled={saving}>
                    Save
                </button>
                <button type="button" className="media-kit-edit-cancel-btn" onClick={onCancel} disabled={saving}>
                    Cancel
                </button>
            </div>
        </div>
    );
}
